import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { 
  User, FileText, Link as LinkIcon, Linkedin, Globe, 
  Lightbulb, Target, Briefcase, Image, CheckCircle2, Circle
} from 'lucide-react';

const ProfileCompletion = ({ user, profileData }) => {
  const navigate = useNavigate();
  
  // Define profile fields with their weights and labels
  const profileFields = [
    { 
      key: 'username', 
      label: 'Username', 
      icon: User, 
      weight: 15,
      description: 'Create your unique @handle'
    },
    { 
      key: 'bio', 
      label: 'Bio', 
      icon: FileText, 
      weight: 15,
      description: 'Tell others about yourself'
    },
    { 
      key: 'profile_picture_url', 
      label: 'Profile Picture', 
      icon: Image, 
      weight: 10,
      description: 'Add a profile photo'
    },
    { 
      key: 'linkedin_url', 
      label: 'LinkedIn', 
      icon: Linkedin, 
      weight: 10,
      description: 'Connect your LinkedIn'
    },
    { 
      key: 'website_url', 
      label: 'Website', 
      icon: Globe, 
      weight: 10,
      description: 'Share your website'
    },
    { 
      key: 'mission_statement', 
      label: 'Mission Statement', 
      icon: Target, 
      weight: 15,
      description: 'What drives your innovation?'
    },
    { 
      key: 'passion_areas', 
      label: 'Passion Areas', 
      icon: Lightbulb, 
      weight: 10,
      isArray: true,
      description: 'Share what you\'re passionate about'
    },
    { 
      key: 'expertise', 
      label: 'Expertise', 
      icon: Briefcase, 
      weight: 10,
      isArray: true,
      description: 'Highlight your skills'
    },
    { 
      key: 'innovation_areas', 
      label: 'Innovation Areas', 
      icon: Lightbulb, 
      weight: 5,
      isArray: true,
      description: 'Select impact categories'
    }
  ];
  
  // Calculate completion
  const data = profileData || user || {};
  
  const getFieldStatus = (field) => {
    const value = data[field.key];
    if (field.isArray) {
      return value && Array.isArray(value) && value.length > 0;
    }
    return value && value.toString().trim().length > 0;
  };
  
  const completedFields = profileFields.filter(field => getFieldStatus(field));
  const completionPercentage = completedFields.reduce((sum, field) => sum + field.weight, 0);
  
  // Get color based on percentage
  const getProgressColor = (pct) => {
    if (pct < 30) return 'bg-red-500';
    if (pct < 60) return 'bg-orange-500';
    if (pct < 85) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  // Get message based on percentage
  const getMessage = (pct) => {
    if (pct === 100) return { text: "Profile Complete! 🎉", sub: "You're all set to connect with the community" };
    if (pct >= 85) return { text: "Almost there!", sub: "Just a few more details to complete your profile" };
    if (pct >= 60) return { text: "Good progress!", sub: "Complete your profile to stand out" };
    if (pct >= 30) return { text: "Keep going!", sub: "A complete profile helps you connect better" };
    return { text: "Let's get started!", sub: "Complete your profile to unlock full features" };
  };
  
  const message = getMessage(completionPercentage);
  const incompleteFields = profileFields.filter(field => !getFieldStatus(field));
  
  if (completionPercentage === 100) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-green-800">{message.text}</p>
            <p className="text-sm text-green-600">{message.sub}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50 rounded-xl p-5 border border-purple-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-800 text-lg">{message.text}</h3>
          <p className="text-sm text-slate-600">{message.sub}</p>
        </div>
        <div className="text-right">
          <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            {completionPercentage}%
          </span>
          <p className="text-xs text-slate-500">Complete</p>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden mb-4">
        <div 
          className={`h-full ${getProgressColor(completionPercentage)} transition-all duration-500 ease-out rounded-full`}
          style={{ width: `${completionPercentage}%` }}
        />
      </div>
      
      {/* Missing Fields */}
      <div className="space-y-2 mb-4">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Complete these to boost your profile:</p>
        <div className="flex flex-wrap gap-2">
          {incompleteFields.slice(0, 4).map(field => {
            const Icon = field.icon;
            return (
              <div 
                key={field.key}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-slate-200 text-sm text-slate-600 hover:border-purple-300 hover:bg-purple-50 transition-colors cursor-pointer"
                onClick={() => navigate('/profile/edit')}
                title={field.description}
              >
                <Circle className="w-3 h-3 text-slate-300" />
                <Icon className="w-3.5 h-3.5" />
                <span>{field.label}</span>
                <span className="text-xs text-purple-500">+{field.weight}%</span>
              </div>
            );
          })}
          {incompleteFields.length > 4 && (
            <div className="flex items-center px-3 py-1.5 text-sm text-slate-500">
              +{incompleteFields.length - 4} more
            </div>
          )}
        </div>
      </div>
      
      {/* CTA Button */}
      <Button 
        onClick={() => navigate('/profile/edit')}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
      >
        Complete Your Profile
      </Button>
    </div>
  );
};

export default ProfileCompletion;
